/*
 * Copyright (c) 2026. World Wide Generation Ltd
 */

import { useState } from 'react';
import Papa from 'papaparse';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { downloadBlob } from '@utils/downloadBlob';
import type { DataTransferRow, DataTransferSurveyResult } from '@g17eco/types/data-transfer';

interface CsvContext {
  subsidiaryName?: string;
}

interface CsvColumn {
  key: string;
  label: string;
  getValue: (row: DataTransferRow, survey: DataTransferSurveyResult, context: CsvContext) => string | number;
  defaultEnabled: boolean;
}

const CSV_COLUMNS: CsvColumn[] = [
  { key: 'subsidiary', label: 'Subsidiary', getValue: (_r, _s, c) => c.subsidiaryName ?? '', defaultEnabled: true },
  { key: 'survey', label: 'Survey', getValue: (_r, s) => s.label, defaultEnabled: true },
  { key: 'metricCode', label: 'Metric Code', getValue: (r) => r.utrCode, defaultEnabled: true },
  { key: 'optionCode', label: 'Option Code', getValue: (r) => r.optionCode ?? '', defaultEnabled: false },
  { key: 'metric', label: 'Metric', getValue: (r) => r.utrLabel, defaultEnabled: true },
  { key: 'option', label: 'Option', getValue: (r) => r.optionLabel ?? '', defaultEnabled: false },
  { key: 'valueType', label: 'Value Type', getValue: (r) => r.valueType, defaultEnabled: false },
  { key: 'unit', label: 'Unit', getValue: (r) => r.survey.unit ?? '', defaultEnabled: true },
  { key: 'numberScale', label: 'Number Scale', getValue: (r) => r.survey.numberScale ?? '', defaultEnabled: false },
  { key: 'surveyValue', label: 'Survey Value', getValue: (r) => r.survey.value ?? '', defaultEnabled: true },
  { key: 'integrationValue', label: 'Integration Value', getValue: (r) => r.integration.value ?? '', defaultEnabled: true },
  { key: 'valueSource', label: 'Value Source', getValue: (r) => r.integration.source ?? '', defaultEnabled: false },
  { key: 'detail', label: 'Source Detail', getValue: (r) => r.integration.detail ?? '', defaultEnabled: false },
  { key: 'surveyStatus', label: 'Survey Status', getValue: (r) => r.survey.status, defaultEnabled: false },
  { key: 'mappingStatus', label: 'Mapping Status', getValue: (r) => r.status, defaultEnabled: true },
  { key: 'discrepancy', label: 'Has Discrepancy', getValue: (r) => r.hasDiscrepancy ? 'yes' : '', defaultEnabled: true },
  { key: 'internalNotes', label: 'Internal Notes', getValue: (r) => r.internalNotes ?? '', defaultEnabled: false },
  { key: 'partnerNotes', label: 'Partner Notes', getValue: (r) => r.partnerNotes ?? '', defaultEnabled: false },
];

const buildCsv = (surveys: DataTransferSurveyResult[], columns: CsvColumn[], context: CsvContext): string => {
  const fields = columns.map((c) => c.label);
  const data = surveys.flatMap((s) =>
    s.rows.map((r) => {
      const row: Record<string, string | number> = {};
      for (const col of columns) {
        row[col.label] = col.getValue(r, s, context);
      }
      return row;
    }),
  );
  return Papa.unparse({ fields, data });
};

const STORAGE_KEY = 'data-transfer-csv-columns';

const loadSavedColumns = (): Set<string> => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const keys = JSON.parse(saved) as string[];
      if (Array.isArray(keys) && keys.length > 0) {
        return new Set(keys);
      }
    }
  } catch {
    // ignore
  }
  return new Set(CSV_COLUMNS.filter((c) => c.defaultEnabled).map((c) => c.key));
};

const saveColumns = (keys: Set<string>) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...keys]));
};

interface ColumnModalProps {
  onExport: (columns: CsvColumn[]) => void;
  onClose: () => void;
}

const ColumnModal = ({ onExport, onClose }: ColumnModalProps) => {
  const [enabled, setEnabled] = useState<Set<string>>(loadSavedColumns);

  const toggle = (key: string) => {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleExport = () => {
    saveColumns(enabled);
    onExport(CSV_COLUMNS.filter((c) => enabled.has(c.key)));
    onClose();
  };

  return (
    <Modal isOpen toggle={onClose} backdrop='static'>
      <ModalHeader toggle={onClose}>Select CSV Columns</ModalHeader>
      <ModalBody>
        <div className='d-flex gap-2 mb-3'>
          <Button color='link' size='sm' className='p-0' onClick={() => setEnabled(new Set(CSV_COLUMNS.map((c) => c.key)))}>
            Select all
          </Button>
          <Button color='link' size='sm' className='p-0' onClick={() => setEnabled(new Set())}>
            Deselect all
          </Button>
        </div>
        {CSV_COLUMNS.map((col) => (
          <div key={col.key} className='form-check'>
            <input
              className='form-check-input'
              type='checkbox'
              id={`csv-col-${col.key}`}
              checked={enabled.has(col.key)}
              onChange={() => toggle(col.key)}
            />
            <label className='form-check-label' htmlFor={`csv-col-${col.key}`}>
              {col.label}
            </label>
          </div>
        ))}
      </ModalBody>
      <ModalFooter>
        <Button color='secondary' size='sm' onClick={onClose}>Cancel</Button>
        <Button color='primary' size='sm' onClick={handleExport} disabled={enabled.size === 0}>
          <i className='fa fa-download me-1' />Download ({enabled.size} columns)
        </Button>
      </ModalFooter>
    </Modal>
  );
};

interface DataTransferCsvExportProps {
  surveys: DataTransferSurveyResult[];
  filename: string;
  subsidiaryName?: string;
}

export const DataTransferCsvExport = ({ surveys, filename, subsidiaryName }: DataTransferCsvExportProps) => {
  const [showModal, setShowModal] = useState(false);

  const handleExport = (columns: CsvColumn[]) => {
    const csv = buildCsv(surveys, columns, { subsidiaryName });
    downloadBlob(new Blob([csv], { type: 'text/csv' }), filename);
  };

  return (
    <>
      <Button color='secondary' outline size='sm' onClick={() => setShowModal(true)}>
        <i className='fa fa-file-text-o me-1' />
        Download CSV
      </Button>
      {showModal && (
        <ColumnModal onExport={handleExport} onClose={() => setShowModal(false)} />
      )}
    </>
  );
};
